// src/pages/Stock/StockInventory.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stockService, productService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    EyeIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';
import AdjustStockModal from './AdjustStockModal';
import ProductStockModal from './ProductStockModal';

const StockInventory = () => {
    const { hasAnyRole } = useAuth();
    const [stock, setStock] = useState({
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 100,
        total: 0,
        from: null,
        to: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterVariant, setFilterVariant] = useState('');
    const [showAvailableOnly, setShowAvailableOnly] = useState(false);
    const [sortConfig, setSortConfig] = useState({
                                                     key: 'product.sku',
                                                     direction: 'asc'
                                                 });
    const [adjustModalOpen, setAdjustModalOpen] = useState(false);
    const [productStockModalOpen, setProductStockModalOpen] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch stock inventory
    useEffect(() => {
        fetchStock();
    }, [filterVariant, showAvailableOnly, currentPage, sortConfig]);

    const fetchStock = async (pageParams = {}) => {
        try {
            setLoading(true);
            const params = {
                variant: filterVariant,
                available: showAvailableOnly ? 1 : 0,
                search: searchTerm,
                page: pageParams.page || currentPage,
                per_page: 100,
                sort_by: sortConfig.key,
                sort_direction: sortConfig.direction
            };
            const response = await stockService.getStock(params);
            // response.data already contains { data, meta, links }
            setStock(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching stock inventory:', err);
            setError('Failed to load stock inventory. Please try again later.');
            toast.error('Failed to load stock inventory');
        } finally {
            setLoading(false);
        }
    };

    // Sort stock
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Apply search
    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1); // Reset to first page when searching
        fetchStock({ page: 1 });
    };

    // Handle adjust stock click
    const handleAdjustClick = (item) => {
        setSelectedStock(item);
        setAdjustModalOpen(true);
    };

    // Open add stock modal
    const handleAddStockClick = () => {
        setSelectedStock(null);
        setProductStockModalOpen(true);
    };

    // Confirm adjust stock
    const confirmAdjust = async (stockId, newQuantity, notes) => {
        try {
            await stockService.adjustStock({
                                               stock_id: stockId,
                                               quantity: newQuantity,
                                               notes
                                           });

            fetchStock(); // Refresh the list
            toast.success('Stock adjusted successfully');
        } catch (error) {
            console.error('Error adjusting stock:', error);
            toast.error(error.response?.data?.message || 'Failed to adjust stock');
        } finally {
            setAdjustModalOpen(false);
            setSelectedStock(null);
        }
    };

    // Handle pagination
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        fetchStock({ page: newPage });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap sm:flex-nowrap">
                <h1 className="text-2xl font-semibold text-gray-800 mb-3 sm:mb-0">Stock Inventory</h1>

                <div className="flex space-x-2 ">
                    <button
                        onClick={() => fetchStock()}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <ArrowPathIcon className="h-5 w-5 mr-2" /> Refresh
                    </button>

                    {hasAnyRole(['admin', 'production']) && (
                        <>
                            <button
                                onClick={handleAddStockClick}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" /> Add/Remove Stock
                            </button>

                            <Link
                                to="/production/new"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" /> Add Production Batch
                            </Link>
                        </>
                    )}
                </div>
            </div>

            {/* Search and filter */}
            <div className="bg-white p-4 rounded-lg shadow">
                <form onSubmit={handleSearch} className="space-y-4">
                    <div className="flex flex-wrap items-center space-x-4">
                        <div className="w-full max-w-xs">
                            <label htmlFor="search" className="sr-only">
                                Search
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="search"
                                    name="search"
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    placeholder="Search by SKU or name"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="variant" className="sr-only">
                                Variant
                            </label>
                            <select
                                id="variant"
                                name="variant"
                                value={filterVariant}
                                onChange={(e) => setFilterVariant(e.target.value)}
                                className="block w-full py-2 pl-3 pr-10 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                            >
                                <option value="">All Variants</option>
                                <option value="standard">Standard</option>
                                <option value="carbon">Carbon</option>
                            </select>
                        </div>

                        <div className="flex items-center">
                            <input
                                id="available-only"
                                name="available-only"
                                type="checkbox"
                                checked={showAvailableOnly}
                                onChange={(e) => setShowAvailableOnly(e.target.checked)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label htmlFor="available-only" className="ml-2 block text-sm text-gray-900">
                                Available Stock Only
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Search
                        </button>
                    </div>
                </form>
            </div>

            {/* Stock List */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('product.sku')}
                            >
                                <div className="flex items-center">
                                    SKU
                                    {sortConfig.key === 'product.sku' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('product.name')}
                            >
                                <div className="flex items-center">
                                    Product Name
                                    {sortConfig.key === 'product.name' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('variant')}
                            >
                                <div className="flex items-center">
                                    Variant
                                    {sortConfig.key === 'variant' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('quantity')}
                            >
                                <div className="flex items-center">
                                    Total Quantity
                                    {sortConfig.key === 'quantity' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('allocated_quantity')}
                            >
                                <div className="flex items-center">
                                    Allocated
                                    {sortConfig.key === 'allocated_quantity' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                onClick={() => requestSort('available_quantity')}
                            >
                                <div className="flex items-center">
                                    Available
                                    {sortConfig.key === 'available_quantity' && (
                                        <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {stock.data && stock.data.length > 0 ? (
                            stock.data.map((item) => (
                                <tr key={item.id}  className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600">
                                        <Link to={`/stock/${item.id}`} className="hover:underline">
                                            {item.product.sku}
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.product.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            item.variant === 'carbon'
                                            ? 'bg-indigo-100 text-indigo-800'
                                            : 'bg-green-100 text-green-800'
                                        }`}>
                                            {item.variant.charAt(0).toUpperCase() + item.variant.slice(1)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.quantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.allocated_quantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <span className={`font-medium ${
                                            item.available_quantity > 0
                                            ? 'text-green-600'
                                            : 'text-red-600'
                                        }`}>
                                            {item.available_quantity}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end space-x-2">
                                            <Link
                                                to={`/stock/${item.id}`}
                                                className="text-primary-600 hover:text-primary-900"
                                                title="View Details"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </Link>

                                            {hasAnyRole(['admin', 'production']) && (
                                                <button
                                                    onClick={() => handleAdjustClick(item)}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                    title="Adjust Stock"
                                                >
                                                    <PencilIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                             <tr>
                                 <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                     {searchTerm || filterVariant ? 'No matching stock found.' : 'No stock inventory found.'}
                                 </td>
                             </tr>
                         )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {stock.data && stock.total > 0 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => handlePageChange(stock.current_page - 1)}
                                disabled={stock.current_page === 1}
                                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                    stock.current_page === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(stock.current_page + 1)}
                                disabled={stock.current_page === stock.last_page}
                                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                    stock.current_page === stock.last_page
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{stock.from || 0}</span> to{' '}
                                    <span className="font-medium">{stock.to || 0}</span> of{' '}
                                    <span className="font-medium">{stock.total || 0}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => handlePageChange(stock.current_page - 1)}
                                        disabled={stock.current_page === 1}
                                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                            stock.current_page === 1
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="sr-only">Previous</span>
                                        &larr;
                                    </button>

                                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                        Page {stock.current_page || 1} of {stock.last_page || 1}
                                    </span>

                                    <button
                                        onClick={() => handlePageChange(stock.current_page + 1)}
                                        disabled={stock.current_page === stock.last_page}
                                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                            stock.current_page === stock.last_page
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="sr-only">Next</span>
                                        &rarr;
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Adjust Stock Modal */}
            {selectedStock && (
                <AdjustStockModal
                    isOpen={adjustModalOpen}
                    onClose={() => setAdjustModalOpen(false)}
                    onConfirm={confirmAdjust}
                    stockItem={selectedStock}
                />
            )}

            {/* Product Stock Modal */}
            <ProductStockModal
                isOpen={productStockModalOpen}
                onClose={() => setProductStockModalOpen(false)}
                onSuccess={() => fetchStock()}
                initialData={null}
            />
        </div>
    );
};

export default StockInventory;
